import React, { useEffect, useRef, useState } from "react";
import "./ContactUs.css";
import axios from "axios";
import { AiOutlineCheckCircle } from "react-icons/ai";
import Nav from "../Home/navbar/nav";
import { Helmet } from "react-helmet";
import IndianFlag from "../Media/indian.png";
import "react-phone-input-2/lib/style.css";
import { countries } from "../constants/countries";
import { isPossiblePhoneNumber } from "libphonenumber-js";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { AiOutlineDownload } from "react-icons/ai";

gsap.registerPlugin(ScrollTrigger);

const ContactUs = () => {
  const phoneInputRef = useRef(null);
  const locations = [
    {
      id: "india",
      flagSrc: IndianFlag,
      flagEmoji: "🇮🇳",
      country: "INDIA",
      addressLines: [
        "6/483 C, Fathima Road East,",
        "Azhagappapuram, Kanyakumari District,",
        "Tamil Nadu - 629401.",
      ],
      gmap: "#",
      contacts: [
        { title: "Sales Inquiry", email: "sales@althisolutions.com" },
        { title: "HR Inquiry", email: "hr@althisolutions.com" },
        { title: "General Inquiry", email: "info@althisolutions.com" },
      ],
    },
  ];

  const [currentMaxDigits, setCurrentMaxDigits] = useState(10); // Defaults to 10 digits
  const [isOpen, setIsOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    organization: "",
    region: "",
    message: "",
    countryCode: "",
    nationalNumber: "",
    fullNumber: "",
  });
  const [phoneError, setPhoneError] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const textOnlyFields = ["fullName", "organization", "region", "message"];

    if (textOnlyFields.includes(name)) {
      const onlyText = value.replace(/[^a-zA-Z\s]/g, "");
      setFormData({
        ...formData,
        [name]: onlyText,
      });
    } else if (name === "phone") {
      const numericAndPlus = value.replace(/[^0-9+\s]/g, "");
      const matchedCountry = countries.find(
        (c) =>
          numericAndPlus.startsWith(`${c.code} `) || numericAndPlus === c.code,
      );
      const maxDigits = matchedCountry
        ? matchedCountry.maxLength
        : currentMaxDigits || 15;
      if (matchedCountry) {
        const codeLength = matchedCountry.code.length + 1;
        if (numericAndPlus.length > codeLength + maxDigits) {
          return;
        }
      }
      setFormData({
        ...formData,
        [name]: numericAndPlus,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

    const handlePhoneChange = (e) => {
      const rawValue = e.target.value;
      const cleanedValue = rawValue.replace(/[^0-9+\s]/g, "");
      if (!cleanedValue.startsWith("+")) {
        handleChange({
          target: {
            name: "phone",
            value: cleanedValue,
          },
        });
        setPhoneError("");
        return;
      }

      const matched = countries
        .filter((country) => cleanedValue.startsWith(country.code))
        .sort((a, b) => b.code.length - a.code.length)[0];

      if (!matched) {
        handleChange({
          target: {
            name: "phone",
            value: cleanedValue,
          },
        });
        setPhoneError("");
        return;
      }

      const phoneDigits = cleanedValue
        .slice(matched.code.length)
        .replace(/\D/g, "");
      const formattedValue = `${matched.code} ${phoneDigits}`;
      handleChange({
        target: {
          name: "phone",
          value: formattedValue,
        },
      });

      if (phoneDigits.length === 0) {
        setPhoneError("");
        return;
      }

      const isValid = isPossiblePhoneNumber(formattedValue);

      if (!isValid) {
        setPhoneError("Please enter a valid phone number.");
      } else {
        setPhoneError("");
      }
    };

  useEffect(() => {
    const handleOutsideClick = () => setIsOpen(false);
    window.addEventListener("click", handleOutsideClick);
    return () => window.removeEventListener("click", handleOutsideClick);
  }, []);
  useEffect(() => {

    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".contactus-form h2",
        {
          opacity: 0,
          y: 60,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: "power3.out",
        },
      );

      gsap.fromTo(
        ".contactus-section-banner-content h1",
        {
          opacity: 0,
          y: 60,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          delay: 0.2,
          ease: "power3.out",
        },
      );

      gsap.fromTo(
        ".locations-title",
        {
          opacity: 0,
          y: 60,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".locations-title",
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        },
      );

      gsap.fromTo(
        ".flag-and-name",
        {
          opacity: 0,
          y: 60,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".flag-and-name",
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        },
      );

      gsap.fromTo(
        ".gmap-link",
        {
          opacity: 0,
          y: 40,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          delay: 0.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".gmap-link",
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        },
      );
    });

    return () => {
      ctx.revert();
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;

    const apiURL = process.env.REACT_APP_CONTACTUS_HOST;
    const hasPhoneInput = formData.phone && formData.phone.trim() !== "";

    if (
      !formData.fullName ||
      !formData.organization ||
      !formData.message ||
      !formData.email ||
      !formData.region ||
      !hasPhoneInput
    ) {
      return;
    }

    const phoneIsValid = isPossiblePhoneNumber(formData.phone);

    if (!phoneIsValid) {
      setPhoneError("Please enter a valid phone number.");
      return;
    }

    setPhoneError("");

    setLoading(true);

    const payload = {
      fullName: formData.fullName,
      email: formData.email,
      organization: formData.organization,
      region: formData.region,
      phone: formData.phone,
      message: formData.message,
    };

    const handleSuccessUI = () => {
      setShowSuccessMessage(true);

      setFormData({
        fullName: "",
        email: "",
        organization: "",
        region: "",
        phone: "",
        message: "",
      });

      setPhoneError("");

      setCurrentMaxDigits(10);

      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
    };

    axios
      .post(apiURL, payload)
      .then((response) => {
        handleSuccessUI();
      })
      .catch((error) => {
        if (error.message === "Network Error" || !error.response) {
          handleSuccessUI();
        } else {
          alert("Server error. Please check your connection and try again.");
          console.error("True API Error:", error);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <>
      <div className="w-full ">
        <Helmet>
          <title>Contact Us | Althi Solutions</title>
          <meta
            name="description"
            content="We are here to help you achieve digital success with IT solutions, expert guidance, and reliable support designed to meet your business needs."
          />
          <link
            rel="canonical"
            href="https://www.althisolutions.com/contactus"
          />
          <meta name="robots" content="index, follow" />

          <meta property="og:type" content="website" />
          <meta property="og:title" content="Contact Us | Althi Solutions" />
          <meta
            property="og:description"
            content="We are here to help you achieve digital success with IT solutions, expert guidance, and reliable support designed to meet your business needs."
          />
          <meta
            property="og:url"
            content="https://www.althisolutions.com/contactus"
          />
          <meta
            property="og:image"
            content="https://www.althisolutions.com/thumbnail.jpg"
          />
          <meta property="og:site_name" content="Althi Solutions" />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@AlthiSolutions" />
          <meta name="twitter:creator" content="@AlthiSolutions" />
          <meta name="twitter:title" content="Contact Us | Althi Solutions" />
          <meta
            name="twitter:description"
            content="We are here to help you achieve digital success with IT solutions, expert guidance, and reliable support designed to meet your business needs."
          />
          <meta
            name="twitter:image"
            content="https://www.althisolutions.com/thumbnail.jpg"
          />
        </Helmet>

        <Nav />

        <div className="contactus-section-banner aln_Pos">
          <div className="contact-overlay">
            {/* <div className='container'> */}
            <div className="contactus-section-overlay flex">
              <div className="max_Width w-full">
                <div className="contactus-section-banner-content-section">
                  <div className="contactus-section-banner-form">
                    <div className="contactus-form">
                      <h2>Let's Talk</h2>
                      <div className="contact-form-container">
                        <form className="contact-form" onSubmit={handleSubmit}>
                          <div className="form-row">
                            <div className="form-group-contact half">
                              <label
                                className="contact-form-label"
                                htmlFor="fullName"
                                style={{ color: "white" }}
                              >
                                Full Name
                              </label>
                              <input
                                type="text"
                                id="fullName"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleChange}
                                required
                              />
                            </div>
                          </div>

                          <div className="form-group-contact">
                            <label
                              className="contact-form-label"
                              htmlFor="email"
                              style={{ color: "white" }}
                            >
                              Email
                            </label>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                            />
                          </div>

                          <div
                            className="form-group-contact"
                            style={{ position: "relative" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <label
                              className="contact-form-label"
                              htmlFor="phone"
                              style={{ color: "white" }}
                            >
                              Phone Number
                            </label>
                            <input
                              type="text"
                              id="phone"
                              name="phone"
                              ref={phoneInputRef}
                              className="contact-form-input"
                              value={formData.phone || ""}
                              onClick={() => setIsOpen(true)}
                              onChange={handlePhoneChange}
                              placeholder="+91 xxxxxxxxxx"
                              required
                            />

                            {phoneError && (
                              <small className="phone-error-message">
                                {phoneError}
                              </small>
                            )}
                          {isOpen && (
  <div
    className="country-dropdown"
    data-lenis-prevent
  >
    {/* Search at the top */}
    <div className="country-search-wrapper">
      <input
        type="text"
        className="country-search-input"
        placeholder="Search country or code..."
        value={countrySearch}
        onChange={(e) => setCountrySearch(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        autoFocus
      />
    </div>

    {/* Country list */}
    <ul className="country-list custom-scrollbar">
      {countries
        .filter((country) =>
          country.name
            .toLowerCase()
            .includes(countrySearch.toLowerCase()) ||
          country.code
            .toLowerCase()
            .includes(countrySearch.toLowerCase())
        )
        .map((country, index) => (
          <li
            key={index}
            onClick={() => {
              setCurrentMaxDigits(country.maxLength);

              handleChange({
                target: {
                  name: "phone",
                  value: `${country.code} `,
                },
              });

              setIsOpen(false);
              setCountrySearch("");

              setTimeout(() => {
                if (phoneInputRef.current) {
                  phoneInputRef.current.focus();
                }
              }, 0);
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.1)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor =
                "transparent")
            }
          >
            {country.name} ({country.code})
          </li>
        ))}
    </ul>
  </div>
)}
                          </div>

                          <div className="form-row">
                            <div className="form-group-contact half">
                              <label
                                className="contact-form-label"
                                htmlFor="organization"
                                style={{ color: "white" }}
                              >
                                Organization
                              </label>
                              <input
                                type="text"
                                id="organization"
                                name="organization"
                                value={formData.organization}
                                onChange={handleChange}
                                required
                              />
                            </div>
                            <div className="form-group-contact half">
                              <label
                                className="contact-form-label"
                                htmlFor="region"
                                style={{ color: "white" }}
                              >
                                Region
                              </label>
                              <input
                                type="text"
                                id="region"
                                name="region"
                                value={formData.region}
                                onChange={handleChange}
                                required
                              />
                            </div>
                          </div>

                          <div className="form-group-contact">
                            <label
                              className="contact-form-label"
                              htmlFor="message"
                              style={{ color: "white" }}
                            >
                              How Can We Help You...
                            </label>
                            <input
                              id="message"
                              name="message"
                              value={formData.message}
                              onChange={handleChange}
                              required
                            />
                          </div>

                          {showSuccessMessage && (
                            <div
                              className="success-messages"
                              style={{ marginBottom: "15px" }}
                            >
                              <div
                                className="success-message-contents"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#4BB543",
                                }}
                              >
                                <div className="success-message-headings">
                                  Submitted Successfully!
                                </div>
                                <AiOutlineCheckCircle className="check-icon" />
                              </div>
                            </div>
                          )}

                          <div className="form-group-contact submit-group">
                            <button
                              type="submit"
                              disabled={loading}
                              className={
                                loading ? "submit-btnn loading" : "submit-btnn"
                              }
                            >
                              {loading ? "Submitting..." : "Submit"}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>

                  <div className="contactus-section-banner-content ">
                    <h1 className="">Request for Service</h1>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <section className="locations-section">
          <div className="container">
            <h2 className="locations-title">Our Locations</h2>
            <p className="locations-sub">
              From questions about pricing to personalized one-on-one demos,
              we’re here to help you get started.
            </p>

            <div className="locations-list">
              {locations.map((loc) => (
                <article className="location-row" key={loc.id}>
                  {/* LEFT SIDE */}
                  <div className="location-left">
                    <div className="flag-and-name">
                      {loc.flagSrc ? (
                        <img
                          src={loc.flagSrc}
                          alt={
                            loc.country ? `${loc.country} flag` : "Country flag"
                          }
                          className="flag-image"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flag-emoji">{loc.flagEmoji}</div>
                      )}
                      <h3 className="country-name-flag">{loc.country}</h3>
                    </div>

                    <address className="address-block">
                      {loc.addressLines.map((line, idx) => (
                        <div key={idx}>{line}</div>
                      ))}
                    </address>
                    
                    <div className="location-actions">
                      <a
                        className="gmap-link"
                        href="https://www.google.com/maps/search/?api=1&query=ALTHI+SOLUTIONS+6%2F483C+Fatima+Road+East+Azhagappapuram+Tamil+Nadu+629401"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="pin">📍</span>
                        <span>Reach Us</span>
                      </a>
                    <a
                      className="brochure-card"
                      href="/Althi_Brochure-compressed.pdf"
                      download="Althi_Brochure-compressed.pdf"
                      aria-label="Download Althi Solutions company brochure"
                    >
                      <div className="brochure-preview">
                        <img
                          src="/althi_brochure_big.png"
                          alt="Althi Solutions brochure preview"
                          loading="lazy"
                        />
                      </div>

                      <div className="brochure-content">
                        {/* <span className="brochure-eyebrow">COMPANY PROFILE</span> */}
                        <h4>Althi Solutions</h4>
                        <p>Explore our services and expertise</p>

                        <span className="brochure-download-action">
                          Download Brochure
                          <AiOutlineDownload />
                        </span>
                      </div>
                    </a>
                    </div>
                  </div>

                  {/* RIGHT SIDE */}
                  <div className="location-right">
                    {loc.contacts.map((c, i) => (
                      <div className="contact-card" key={i}>
                        <div className="avatar-placeholder" aria-hidden="true">
                          👤
                        </div>
                        <div className="contact-right">
                          <h4 className="contact-title">{c.title}</h4>
                          <a
                            className="contact-email"
                            href={`mailto:${c.email}`}
                          >
                            {c.email}
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactUs;
